/**
 * ZitadelPasswordActions — admin password controls for a single user, sitting
 * next to the Zitadel lock/unlock control in the UserDetailDrawer.
 *
 * Two actions, both via the BE ``admin/v2`` Zitadel ops (User Service v2):
 *   - **Invia email di reset** → ``sendZitadelPasswordReset`` → Zitadel emails
 *     the user a reset link; the user sets their own password (admin never
 *     sees it). The safest path — recommended default.
 *   - **Imposta nuova password** → ``setZitadelPassword`` → admin sets a
 *     password directly (typed or generated), optionally forcing a change at
 *     next login. Zitadel still enforces the org complexity policy and
 *     rejects weak values (surfaced as a toast).
 *
 * DRY: reuses the shared ``PasswordStrengthMeter`` (orgs) + ``@ando/ui``
 * Dialog/Switch + ``useToast``; mirrors the inline-handler + toast vocabulary
 * already used by the unlock control.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Copy, Eye, EyeOff, KeyRound, Loader2, Mail, RefreshCw } from "lucide-react";
import * as React from "react";

import { sendZitadelPasswordReset, setZitadelPassword } from "@ando/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Switch,
  cn,
  useToast,
} from "@ando/ui";

import { PasswordStrengthMeter } from "./orgs/PasswordStrengthMeter";

interface ZitadelPasswordActionsProps {
  readonly userId: string;
}

/** Generate a 16-char password guaranteed to hit all 4 charset classes so it
 *  satisfies the default Zitadel complexity policy out of the box. */
function generateStrongPassword(): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digit = "23456789";
  const symbol = "!@#$%*?-_";
  const all = upper + lower + digit + symbol;
  const randIdx = (n: number): number => (crypto.getRandomValues(new Uint32Array(1))[0] ?? 0) % n;
  // ``charAt`` always returns a string (unlike ``[idx]`` under
  // noUncheckedIndexedAccess), keeping the array strictly ``string[]``.
  const pick = (set: string): string => set.charAt(randIdx(set.length));
  const chars: string[] = [pick(upper), pick(lower), pick(digit), pick(symbol)];
  while (chars.length < 16) chars.push(pick(all));
  // Fisher–Yates shuffle with CSPRNG so the guaranteed-class chars aren't
  // always in the first four positions.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randIdx(i + 1);
    const tmp = chars[i] ?? "";
    chars[i] = chars[j] ?? "";
    chars[j] = tmp;
  }
  return chars.join("");
}

export function ZitadelPasswordActions({
  userId,
}: ZitadelPasswordActionsProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();

  const [resetBusy, setResetBusy] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [reveal, setReveal] = React.useState(false);
  const [changeRequired, setChangeRequired] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const handleResetEmail = async (): Promise<void> => {
    setResetBusy(true);
    try {
      await sendZitadelPasswordReset(userId);
      toast.success(i18n._(msg`Email di reset inviata`), {
        description: i18n._(msg`L'utente riceverà un link per impostare una nuova password.`),
      });
    } catch (err) {
      toast.error(i18n._(msg`Invio email fallito`), {
        description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
      });
    } finally {
      setResetBusy(false);
    }
  };

  const handleGenerate = (): void => {
    setPassword(generateStrongPassword());
    setReveal(true);
  };

  const handleCopy = (): void => {
    if (!password) return;
    const clip = navigator.clipboard;
    if (!clip?.writeText) {
      toast.error(i18n._(msg`Appunti non disponibili in questo contesto`));
      return;
    }
    // Toast only on the ACTUAL write outcome (the `?.` short-circuit used to
    // fire a success toast even when the clipboard API was missing).
    clip.writeText(password).then(
      () =>
        toast.success(i18n._(msg`Password copiata`), {
          description: i18n._(msg`Contiene un segreto — incollala subito e svuota gli appunti.`),
        }),
      () => toast.error(i18n._(msg`Copia negli appunti fallita`)),
    );
  };

  const resetDialog = (): void => {
    setPassword("");
    setReveal(false);
    setChangeRequired(true);
  };

  const handleSetPassword = async (): Promise<void> => {
    if (password.length < 8) return;
    setSaving(true);
    try {
      await setZitadelPassword(userId, {
        password,
        change_required: changeRequired,
      });
      toast.success(i18n._(msg`Password aggiornata`), {
        description: changeRequired
          ? i18n._(msg`L'utente dovrà cambiarla al prossimo accesso.`)
          : i18n._(msg`La nuova password è attiva.`),
      });
      setDialogOpen(false);
      resetDialog();
    } catch (err) {
      // Zitadel 400 on weak password surfaces here verbatim.
      toast.error(i18n._(msg`Impostazione password fallita`), {
        description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        onClick={handleResetEmail}
        disabled={resetBusy}
        className="min-h-[36px] shadow-none hover:shadow-none"
      >
        {resetBusy ? (
          <Loader2 size={12} className="mr-1 animate-spin" aria-hidden />
        ) : (
          <Mail size={12} className="mr-1" aria-hidden />
        )}
        <Trans>Invia email di reset</Trans>
      </Button>

      <Button
        size="sm"
        variant="secondary"
        onClick={() => setDialogOpen(true)}
        className="min-h-[36px] shadow-none hover:shadow-none"
      >
        <KeyRound size={12} className="mr-1" aria-hidden />
        <Trans>Imposta nuova password</Trans>
      </Button>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Trans>Imposta nuova password</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Imposti direttamente una password per l'utente. Zitadel applica comunque la policy
                di complessità dell'organizzazione.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div>
              <label
                htmlFor="zitadel-new-password"
                className="mb-1 block font-medium text-ink-2 text-xs"
              >
                <Trans>Nuova password</Trans>
              </label>
              <div className="flex items-center gap-1.5">
                <div className="relative flex-1">
                  <input
                    id="zitadel-new-password"
                    type={reveal ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    spellCheck={false}
                    className={cn(
                      "w-full rounded-md border border-hairline bg-card px-2.5 py-2 pr-9 text-ink-1 text-sm",
                      "focus-visible:outline-none",
                      "min-h-[40px]",
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((v) => !v)}
                    aria-label={
                      reveal ? i18n._(msg`Nascondi password`) : i18n._(msg`Mostra password`)
                    }
                    className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-ink-3 hover:text-ink-1"
                  >
                    {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleGenerate}
                  className="min-h-[40px] shrink-0"
                  title={i18n._(msg`Genera password forte`)}
                >
                  <RefreshCw size={13} className="mr-1" aria-hidden />
                  <Trans>Genera</Trans>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleCopy}
                  disabled={!password}
                  aria-label={i18n._(msg`Copia password`)}
                  className="min-h-[40px] w-9 shrink-0 p-0"
                >
                  <Copy size={14} aria-hidden />
                </Button>
              </div>
              <div className="mt-2">
                <PasswordStrengthMeter password={password} />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-md border border-hairline bg-card/55 px-3 py-2">
              <span className="text-ink-1 text-xs">
                <Trans>Richiedi cambio al prossimo accesso</Trans>
              </span>
              <Switch
                checked={changeRequired}
                onCheckedChange={setChangeRequired}
                aria-label={i18n._(msg`Richiedi cambio password al prossimo accesso`)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDialogOpen(false);
                resetDialog();
              }}
            >
              <Trans>Annulla</Trans>
            </Button>
            <Button
              type="button"
              onClick={handleSetPassword}
              disabled={saving || password.length < 8}
            >
              {saving ? (
                <Loader2 size={13} className="mr-1 animate-spin" aria-hidden />
              ) : (
                <KeyRound size={13} className="mr-1" aria-hidden />
              )}
              <Trans>Imposta password</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
